import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { FAScores, Assessment } from '../types';
import { Layout } from '../components/Layout';
import { RatingSlider } from '../components/RatingSlider';
import { assessmentService } from '../services/assessmentService';
import { teamService } from '../services/teamService';
import { Card, Button, Label, ErrorMessage, FlexContainer } from '../styles/GlobalStyles';
import { LoadingSpinner } from '../components/LoadingSpinner';

const PageTitle = styled.h1`
  color: var(--text-primary);
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: var(--spacing-xl);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
`;

const SectionCard = styled(Card)`
  margin-bottom: var(--spacing-lg);
`;

const SectionTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
`;

const SectionIcon = styled.div<{ color: string }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.875rem;
`;

const AttributeGrid = styled.div`
  display: grid;
  gap: var(--spacing-lg);
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const AttributeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-family: inherit;
  font-size: 1rem;
  resize: vertical;
  
  &:focus {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    outline: none;
  }
`;

const ButtonGroup = styled(FlexContainer)`
  justify-content: flex-end;
  gap: var(--spacing-md);
  margin-top: var(--spacing-xl);
  
  @media (max-width: 767px) {
    flex-direction: column;
  }
`;



const ProgressIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-xs);
  margin-bottom: var(--spacing-xl);
`;

const ProgressDot = styled.div<{ $active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.$active ? 'var(--primary-color)' : 'var(--border-color)'};
  transition: background-color 0.2s ease;
`;

// Player Skills Development structure - Outfield players
const FA_MODEL_OUTFIELD = {
  technical: {
    title: 'Technical',
    color: '#ef4444',
    icon: 'T',
    attributes: [
      { key: 'ball_control', label: 'Ball Control' },
      { key: 'passing_accuracy', label: 'Passing Accuracy' },
      { key: 'positioning', label: 'Positioning' },
      { key: 'tactical_awareness', label: 'Tactical Awareness' },
      { key: 'shooting', label: 'Shooting' },
      { key: 'dribbling', label: 'Dribbling' },
    ]
  },
  physical: {
    title: 'Physical',
    color: '#dc2626',
    icon: 'P',
    attributes: [
      { key: 'speed', label: 'Speed' },
      { key: 'endurance', label: 'Endurance' },
      { key: 'strength', label: 'Strength' },
      { key: 'agility', label: 'Agility' },
      { key: 'coordination', label: 'Coordination' },
      { key: 'balance', label: 'Balance' },
    ]
  },
  psychological: {
    title: 'Psychological',
    color: '#059669',
    icon: 'M',
    attributes: [
      { key: 'confidence', label: 'Confidence' },
      { key: 'resilience', label: 'Resilience' },
      { key: 'decision_making', label: 'Decision Making' },
      { key: 'concentration', label: 'Concentration' },
      { key: 'motivation', label: 'Motivation' },
      { key: 'composure', label: 'Composure' },
    ]
  },
  social: {
    title: 'Social',
    color: '#3b82f6',
    icon: 'S',
    attributes: [
      { key: 'teamwork', label: 'Teamwork' },
      { key: 'communication', label: 'Communication' },
      { key: 'respect', label: 'Respect' },
      { key: 'leadership', label: 'Leadership' },
      { key: 'coachability', label: 'Coachability' },
      { key: 'sportsmanship', label: 'Sportsmanship' },
    ]
  }
};

// Player Skills Development structure - Goalkeepers
const FA_MODEL_GOALKEEPER = {
  technical: {
    title: 'Technical (Goalkeeper)',
    color: '#ef4444',
    icon: 'T',
    attributes: [
      { key: 'shot_stopping', label: 'Shot Stopping' },
      { key: 'handling', label: 'Handling' },
      { key: 'distribution', label: 'Distribution' },
      { key: 'positioning', label: 'Positioning' },
      { key: 'aerial_ability', label: 'Aerial Ability' },
      { key: 'one_on_one', label: 'One-on-One' },
      { key: 'command_of_area', label: 'Command of Area' },
      { key: 'tactical_awareness', label: 'Tactical Awareness' },
    ]
  },
  physical: {
    title: 'Physical',
    color: '#eab308',
    icon: 'P',
    attributes: [
      { key: 'speed', label: 'Speed' },
      { key: 'endurance', label: 'Endurance' },
      { key: 'strength', label: 'Strength' },
      { key: 'agility', label: 'Agility' },
      { key: 'coordination', label: 'Coordination' },
      { key: 'balance', label: 'Balance' },
    ]
  },
  psychological: {
    title: 'Psychological',
    color: '#10b981',
    icon: 'M',
    attributes: [
      { key: 'confidence', label: 'Confidence' },
      { key: 'resilience', label: 'Resilience' },
      { key: 'decision_making', label: 'Decision Making' },
      { key: 'concentration', label: 'Concentration' },
      { key: 'motivation', label: 'Motivation' },
      { key: 'composure', label: 'Composure' },
    ]
  },
  social: {
    title: 'Social',
    color: '#3b82f6',
    icon: 'S',
    attributes: [
      { key: 'teamwork', label: 'Teamwork' },
      { key: 'communication', label: 'Communication' },
      { key: 'respect', label: 'Respect' },
      { key: 'leadership', label: 'Leadership' },
      { key: 'coachability', label: 'Coachability' },
      { key: 'sportsmanship', label: 'Sportsmanship' },
    ]
  }
};

type AssessmentFormData = {
  scores: FAScores;
  notes?: string;
};

export const AssessmentPage: React.FC = () => {
  const { teamId, playerId } = useParams<{ teamId: string; playerId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string>('');
  const [currentSection, setCurrentSection] = useState(0);
  const [previousAssessment, setPreviousAssessment] = useState<Assessment | null>(null);
  const [draftAssessment, setDraftAssessment] = useState<Assessment | null>(null);
  const [playerName, setPlayerName] = useState<string>('Loading...');
  const [player, setPlayer] = useState<any>(null);
  const [isGoalkeeper, setIsGoalkeeper] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<'MAIN_COACH' | 'COLLABORATING_COACH' | undefined>(undefined);
  
  // Check if user is a collaborating coach (read-only access)
  const isCollaboratingCoach = userRole === 'COLLABORATING_COACH';

  // Select the appropriate model based on player position
  const FA_MODEL = isGoalkeeper ? FA_MODEL_GOALKEEPER : FA_MODEL_OUTFIELD;
  const sections = Object.keys(FA_MODEL);

  // Generate default values based on player position
  const getDefaultScores = () => {
    if (isGoalkeeper) {
      return {
        technical: {
          shot_stopping: 5,
          handling: 5,
          distribution: 5,
          positioning: 5,
          aerial_ability: 5,
          one_on_one: 5,
          command_of_area: 5,
          tactical_awareness: 5,
        },
        physical: {
          speed: 5,
          endurance: 5,
          strength: 5,
          agility: 5,
          coordination: 5,
          balance: 5,
        },
        psychological: {
          confidence: 5,
          resilience: 5,
          decision_making: 5,
          concentration: 5,
          motivation: 5,
          composure: 5,
        },
        social: {
          teamwork: 5,
          communication: 5,
          respect: 5,
          leadership: 5,
          coachability: 5,
          sportsmanship: 5,
        },
      };
    } else {
      return {
        technical: {
          ball_control: 5,
          passing_accuracy: 5,
          positioning: 5,
          tactical_awareness: 5,
          shooting: 5,
          dribbling: 5,
        },
        physical: {
          speed: 5,
          endurance: 5,
          strength: 5,
          agility: 5,
          coordination: 5,
          balance: 5,
        },
        psychological: {
          confidence: 5,
          resilience: 5,
          decision_making: 5,
          concentration: 5,
          motivation: 5,
          composure: 5,
        },
        social: {
          teamwork: 5,
          communication: 5,
          respect: 5,
          leadership: 5,
          coachability: 5,
          sportsmanship: 5,
        },
      };
    }
  };

  const {
    control,
    handleSubmit,
    reset,
    getValues,
  } = useForm<AssessmentFormData>({
    defaultValues: {
      scores: getDefaultScores() as FAScores,
      notes: '',
    },
  });

  useEffect(() => {
    console.log('AssessmentPage useEffect triggered', { playerId, teamId });
    if (playerId && teamId) {
      console.log('Loading assessment data...');
      checkTeamAccess();
      loadPlayerData();
      loadPreviousAssessment();
      loadDraftAssessment();
    } else {
      console.log('Missing playerId or teamId', { playerId, teamId });
    }
  }, [playerId, teamId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const checkTeamAccess = async () => {
    if (!teamId) return;
    
    try {
      const teamData = await teamService.getTeam(teamId);
      setUserRole(teamData.userRole);
    } catch (error) {
      console.error('Failed to check team access:', error);
    }
  };

  const loadPlayerData = async () => {
    if (!playerId || !teamId) {
      console.log('Missing playerId or teamId:', { playerId, teamId });
      return;
    }

    try {
      console.log('Loading players for team:', teamId);
      const players = await teamService.getTeamPlayers(teamId);
      console.log('Found players:', players);

      console.log('=== PLAYER LOOKUP DEBUG ===');
      console.log('Team ID:', teamId);
      console.log('Looking for player ID:', playerId);
      console.log('Players returned from API:', players);
      console.log('Player IDs in response:', players.map(p => p.id));
      console.log('Player details:', players.map(p => ({
        id: p.id,
        name: `${p.firstName} ${p.lastName}`,
        position: p.position
      })));
      
      const foundPlayer = players.find(p => p.id === playerId);
      console.log('Match result:', foundPlayer ? 'FOUND ✅' : 'NOT FOUND ❌');

      if (foundPlayer) {
        setPlayer(foundPlayer);
        const fullName = `${foundPlayer.firstName} ${foundPlayer.lastName}`;
        console.log('Setting player name to:', fullName);
        setPlayerName(fullName);
        
        // Check if player is a goalkeeper
        const isGK = foundPlayer.position?.toLowerCase() === 'goalkeeper';
        setIsGoalkeeper(isGK);
        console.log('Player position:', foundPlayer.position, 'Is goalkeeper:', isGK);
      } else {
        console.log('Player not found in team players');
        setPlayerName('Player Not Found');
      }
    } catch (error) {
      console.error('Failed to load player data:', error);
      setPlayerName('Error Loading Player');
    }
  };

  const loadPreviousAssessment = async () => {
    if (!playerId) {
      console.log('loadPreviousAssessment: No playerId, skipping');
      return;
    }

    try {
      console.log('Loading previous assessments for player:', playerId);
      const assessments = await assessmentService.getPlayerAssessments(playerId);
      console.log('Previous assessments loaded:', assessments);

      // Only consider COMPLETED assessments as "previous assessments"
      // Incomplete/draft assessments should not affect the assessment type
      const completedAssessments = assessments.filter(a => a.isComplete);
      if (completedAssessments.length > 0) {
        console.log('Found completed assessment(s), setting previousAssessment');
        // Use the most recent completed assessment
        setPreviousAssessment(completedAssessments[0]);
      } else {
        console.log('No completed assessments found - this will be an INITIAL assessment');
      }
    } catch (error: any) {
      console.error('Error loading previous assessments:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        code: error.code
      });
      // If it's a 404, that's fine - no previous assessments
      // If it's a 401, that's a real problem - log it but continue
      if (error.response?.status === 401) {
        console.error('Authentication error loading assessments - this should not happen');
      }
    }
  };

  const loadDraftAssessment = async () => {
    if (!playerId) return;

    try {
      const draft = await assessmentService.getDraftAssessment(playerId);
      if (draft) {
        setDraftAssessment(draft);
        // Load draft data into form
        reset({
          scores: draft.scores,
          notes: draft.notes || '',
        });
      }
    } catch (error) {
      // Ignore error - no draft assessment
    }
  };

  // Reset form when player position changes (goalkeeper vs outfield)
  useEffect(() => {
    if (player && !draftAssessment) {
      reset({
        scores: getDefaultScores() as FAScores,
        notes: '',
      });
    }
  }, [isGoalkeeper]); // eslint-disable-line react-hooks/exhaustive-deps



  const onSubmit = async (data: AssessmentFormData) => {
    console.log('onSubmit called with data:', data);
    console.log('Current section:', currentSection, 'Last section index:', sections.length - 1);

    if (!playerId) return;

    setIsLoading(true);
    setServerError('');

    try {
      const assessmentType = previousAssessment ? 'FOLLOW_UP' : 'INITIAL';

      if (draftAssessment) {
        // Complete existing draft
        await assessmentService.completeDraft(draftAssessment.id, {
          playerId,
          type: assessmentType,
          scores: data.scores as FAScores,
          notes: data.notes,
        });
      } else {
        // Create new completed assessment
        await assessmentService.createAssessment({
          playerId,
          type: assessmentType,
          scores: data.scores as FAScores,
          notes: data.notes,
          isComplete: true,
        });
      }

      // Navigate back to player page
      navigate(`/team/${teamId}/player/${playerId}`);
    } catch (error: any) {
      setServerError(error.response?.data?.error?.message || 'Failed to save assessment');
    } finally {
      setIsLoading(false);
    }
  };

  const saveDraft = async () => {
    if (!playerId) return;

    setIsLoading(true);
    setServerError('');

    try {
      const formData = getValues();
      const assessmentType = previousAssessment ? 'FOLLOW_UP' : 'INITIAL';

      if (draftAssessment) {
        // Update existing draft
        await assessmentService.updateDraft(draftAssessment.id, {
          playerId,
          type: assessmentType,
          scores: formData.scores as FAScores,
          notes: formData.notes,
        });
      } else {
        // Create new draft
        const newDraft = await assessmentService.saveDraft({
          playerId,
          type: assessmentType,
          scores: formData.scores as FAScores,
          notes: formData.notes,
        });
        setDraftAssessment(newDraft);
      }

      // Navigate back to player page
      navigate(`/team/${teamId}/player/${playerId}`);
    } catch (error: any) {
      setServerError(error.response?.data?.error?.message || 'Failed to save draft');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelAssessment = () => {
    const confirmed = window.confirm(
      'Are you sure you want to cancel? All progress for this assessment will be lost.'
    );
    
    if (confirmed) {
      // Navigate back to player page without saving
      navigate(`/team/${teamId}/player/${playerId}`);
    }
    // If not confirmed, do nothing and stay on the page
  };

  const nextSection = (event?: React.MouseEvent) => {
    console.log('nextSection called, currentSection:', currentSection, 'sections.length:', sections.length);
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      console.log('Event prevented and stopped');
    }
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
      console.log('Moving to section:', currentSection + 1);
    } else {
      console.log('Already at last section, not moving');
    }
  };

  const prevSection = (event?: React.MouseEvent) => {
    console.log('prevSection called');
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };



  const currentSectionKey = sections[currentSection] as keyof typeof FA_MODEL;
  const currentSectionConfig = FA_MODEL[currentSectionKey];

  // Wait for player data to load before rendering form
  if (playerName === 'Loading...') {
    return <LoadingSpinner />;
  }

  // Show error if player not found
  if (playerName === 'Player Not Found' || playerName === 'Error Loading Player') {
    return (
      <Layout>
        <Card>
          <h2>Player Not Found</h2>
          <p>Unable to load player data. Please try again or go back to the team page.</p>
          <Button onClick={() => window.history.back()}>Go Back</Button>
        </Card>
      </Layout>
    );
  }

  if (!player) {
    return <LoadingSpinner />;
  }

  if (isLoading && !currentSectionConfig) {
    return <LoadingSpinner />;
  }
  
  // Show access denied message for collaborating coaches
  if (isCollaboratingCoach) {
    return (
      <Layout>
        <Card style={{ textAlign: 'center', padding: 'var(--spacing-2xl)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-lg)' }}>🔒</div>
          <PageTitle style={{ marginBottom: 'var(--spacing-md)' }}>Access Restricted</PageTitle>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--spacing-lg)' }}>
            Only the team owner can create or edit assessments. As a collaborating coach, you have read-only access to view player information and assessment history.
          </p>
          <Button 
            as={Link} 
            to={`/team/${teamId}/player/${playerId}`}
            $variant="secondary"
          >
            Back to Player Profile
          </Button>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageTitle>
        {draftAssessment ? 'Resume Assessment' : previousAssessment ? 'Follow-Up Assessment' : 'Initial Assessment'} - {playerName}
        {isGoalkeeper && <span style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginLeft: 'var(--spacing-md)' }}>(Goalkeeper)</span>}
      </PageTitle>



      <ProgressIndicator>
        {sections.map((_, index) => (
          <ProgressDot key={index} $active={index <= currentSection} />
        ))}
      </ProgressIndicator>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <SectionCard>
          <SectionTitle>
            <SectionIcon color={currentSectionConfig.color}>
              {currentSectionConfig.icon}
            </SectionIcon>
            {currentSectionConfig.title}
          </SectionTitle>

          <AttributeGrid>
            {currentSectionConfig.attributes.map((attribute) => (
              <AttributeGroup key={attribute.key}>
                <Label>{attribute.label}</Label>
                <Controller
                  name={`scores.${currentSectionKey}.${attribute.key}` as any}
                  control={control}
                  render={({ field }) => (
                    <RatingSlider
                      value={field.value || 5}
                      onChange={field.onChange}
                      skillKey={attribute.key}
                      previousValue={
                        (previousAssessment?.scores?.[currentSectionKey] as any)?.[attribute.key]
                      }
                    />
                  )}
                />

              </AttributeGroup>
            ))}
          </AttributeGrid>
        </SectionCard>

        {currentSection === sections.length - 1 && (
          <SectionCard>
            <Label htmlFor="notes">Additional Notes (Optional)</Label>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextArea
                  id="notes"
                  {...field}
                  placeholder="Add any additional observations or comments about the player's performance..."
                />
              )}
            />
          </SectionCard>
        )}

        {serverError && <ErrorMessage>{serverError}</ErrorMessage>}

        <ButtonGroup>
          <Button 
            type="button" 
            $variant="secondary" 
            onClick={handleCancelAssessment}
            disabled={isLoading}
          >
            Cancel
          </Button>
          
          {currentSection > 0 && (
            <Button type="button" $variant="secondary" onClick={(e) => prevSection(e)}>
              Previous
            </Button>
          )}

          {currentSection < sections.length - 1 ? (
            <>
              <Button type="button" $variant="secondary" onClick={saveDraft} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button type="button" onClick={(e) => nextSection(e)}>
                Next Section ({currentSection + 1}/{sections.length})
              </Button>
            </>
          ) : (
            <>
              <Button type="button" $variant="secondary" onClick={saveDraft} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Complete Assessment'}
              </Button>
            </>
          )}
        </ButtonGroup>
      </Form>
    </Layout>
  );
};